/**
 * CreneauEditModal — LA modale d'édition de créneau, unique et partagée par TOUTES
 * les briques (service technique, GRC, scolaire, PE, urbanisme, compta).
 *
 * Refonte planning 21/07 : avant, chaque brique avait sa propre modale, certaines
 * SANS bouton Supprimer (GRC, scolaire) → suppression impossible. Cette modale règle
 * la régression : elle contient TOUJOURS un bouton Supprimer, à la couleur de la brique.
 *
 * Base = la CreneauModal du service technique (la seule qui avait déjà tout :
 * Agent · Titre · Début · Fin · Type · Notes · Lieu · [Supprimer]).
 *
 * Ce qui varie d'une brique à l'autre :
 *   - `accent`   : couleur du module → pilote la couleur des boutons/focus.
 *   - `types`    : liste des types propres à la brique (GRC : accueil/événement… ;
 *                  scolaire cantine ; ST : voirie/espaces verts… ; etc.).
 *   - `withLieu` : n'affiche le champ Lieu que pour les briques qui l'ont (ST).
 *   - `lieuSlot` : le sélecteur de lieu de la brique (ex. <LieuSelect/> du ST),
 *                  injecté par le parent pour ne pas coupler ce composant aux lieux.
 *
 * Un créneau posé = présence + travail ensemble (il n'y a qu'UN seul planning).
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { AlertCircle, Trash2, X } from 'lucide-react';
import { Card, Field, Input, Button, Alert } from '@/components/ui';
import type { PlanningAccent } from '@/components/planning/PlanningPresence';

/** Valeurs éditables communes à toutes les briques. */
export type CreneauEditValues = {
  agentUserId: string;
  titre: string;
  description: string | null;
  type: string;
  dateDebut: string; // ISO
  dateFin: string;   // ISO
  lieuId?: string | null;
};

/** Un créneau existant, tel que reçu du back (forme minimale pour préremplir). */
export type CreneauEditInitial = {
  id?: string;
  agentUserId?: string;
  titre?: string;
  description?: string | null;
  type?: string;
  dateDebut?: string;
  dateFin?: string;
  lieuId?: string | null;
};

export type CreneauTypeOption = { value: string; label: string };

/** accent (PlanningAccent) → variant de Button. */
const ACCENT_BTN: Record<PlanningAccent, 'tech' | 'grc' | 'ecole' | 'petite_enfance' | 'urba' | 'compta' | 'rh' | 'asso'> = {
  tech: 'tech',
  grc: 'grc',
  scolaire: 'ecole',
  petite_enfance: 'petite_enfance',
  urbanisme: 'urba',
  comptable: 'compta',
  rh: 'rh',
  asso: 'asso',
};

/** accent → classe de ring focus (listées en dur pour survivre au purge Tailwind). */
const ACCENT_RING: Record<PlanningAccent, string> = {
  tech: 'focus:ring-tech focus:border-tech',
  grc: 'focus:ring-grc focus:border-grc',
  scolaire: 'focus:ring-ecole focus:border-ecole',
  petite_enfance: 'focus:ring-petite_enfance focus:border-petite_enfance',
  urbanisme: 'focus:ring-urba focus:border-urba',
  comptable: 'focus:ring-compta focus:border-compta',
  rh: 'focus:ring-rh focus:border-rh',
  asso: 'focus:ring-asso focus:border-asso',
};

export function CreneauEditModal({
  mode,
  initial,
  agents,
  types,
  accent = 'tech',
  withLieu = false,
  lieuSlot,
  titreLabel = 'Intitulé',
  onClose,
  onSubmit,
  onDelete,
}: {
  mode: 'create' | 'edit';
  initial: CreneauEditInitial;
  agents: Array<{ id: string; fullName: string; role?: string }>;
  types: CreneauTypeOption[];
  accent?: PlanningAccent;
  withLieu?: boolean;
  /** Sélecteur de lieu injecté par la brique (reçoit value/onChange via render prop). */
  lieuSlot?: (value: string | null, onChange: (v: string | null) => void) => ReactNode;
  titreLabel?: string;
  onClose: () => void;
  onSubmit: (values: CreneauEditValues) => Promise<void>;
  /** Présent uniquement en mode edit → affiche le bouton Supprimer. */
  onDelete?: () => Promise<void>;
}) {
  function toLocalInput(iso: string | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    const tz = d.getTimezoneOffset();
    return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 16);
  }

  const [agentUserId, setAgentUserId] = useState(initial.agentUserId ?? agents[0]?.id ?? '');
  const [titre, setTitre] = useState(initial.titre ?? '');
  const [description, setDescription] = useState(initial.description ?? '');
  const [type, setType] = useState(initial.type ?? types[0]?.value ?? 'autre');
  const [dateDebut, setDateDebut] = useState(toLocalInput(initial.dateDebut));
  const [dateFin, setDateFin] = useState(toLocalInput(initial.dateFin));
  const [lieuId, setLieuId] = useState<string | null>(initial.lieuId ?? null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ring = ACCENT_RING[accent] ?? ACCENT_RING.tech;
  const selectCls = `h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong ${ring}`;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!agentUserId || !titre.trim() || !dateDebut || !dateFin) {
      setErr('Agent, intitulé, début et fin sont requis.');
      return;
    }
    setErr(null);
    setSubmitting(true);
    try {
      await onSubmit({
        agentUserId,
        titre: titre.trim(),
        description: description.trim() || null,
        type,
        dateDebut: new Date(dateDebut).toISOString(),
        dateFin: new Date(dateFin).toISOString(),
        ...(withLieu ? { lieuId } : {}),
      });
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Erreur lors de l\'enregistrement');
      setSubmitting(false);
    }
  }

  async function handleDelete() {
    if (!onDelete) return;
    setDeleting(true);
    try {
      await onDelete();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : 'Erreur lors de la suppression');
      setDeleting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 backdrop-blur-md p-4">
      <div className="w-full max-w-md">
        <Card variant="glass" padded>
          <div className="mb-3 flex items-start justify-between gap-2">
            <h2 className="font-display text-base font-semibold tracking-r-snug text-ink">
              {mode === 'create' ? 'Nouveau créneau' : 'Modifier le créneau'}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="rounded-r-md p-1 text-ink-muted transition-colors hover:bg-surface/60 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {err && <Alert variant="error" icon={AlertCircle} className="mb-3">{err}</Alert>}

          <form onSubmit={handleSubmit} className="space-y-3">
            <Field label="Agent" htmlFor="cem-agent" required>
              <select id="cem-agent" value={agentUserId} onChange={(e) => setAgentUserId(e.target.value)} className={selectCls} required>
                <option value="">— Choisir —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.fullName}{a.role ? ` (${a.role})` : ''}</option>
                ))}
              </select>
            </Field>

            <Field label={titreLabel} htmlFor="cem-titre" required>
              <Input id="cem-titre" value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Ex : Accueil public, Tonte stade…" maxLength={200} required />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Début" htmlFor="cem-debut" required>
                <Input id="cem-debut" type="datetime-local" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} required />
              </Field>
              <Field label="Fin" htmlFor="cem-fin" required>
                <Input id="cem-fin" type="datetime-local" value={dateFin} onChange={(e) => setDateFin(e.target.value)} required />
              </Field>
            </div>

            {types.length > 0 && (
              <Field label="Type" htmlFor="cem-type">
                <select id="cem-type" value={type} onChange={(e) => setType(e.target.value)} className={selectCls}>
                  {types.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            )}

            <Field label="Notes" htmlFor="cem-desc">
              <textarea
                id="cem-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Détails ou consignes pour l'agent…"
                className={`w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass p-2.5 text-sm text-ink shadow-soft transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong ${ring}`}
              />
            </Field>

            {withLieu && lieuSlot && (
              <Field label="Lieu (facultatif)" htmlFor="cem-lieu">
                {lieuSlot(lieuId, setLieuId)}
              </Field>
            )}

            <div className="flex items-center justify-between pt-2">
              {mode === 'edit' && onDelete && (
                <Button type="button" variant="danger" size="sm" loading={deleting} onClick={() => void handleDelete()}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> Supprimer
                </Button>
              )}
              <div className="ml-auto flex gap-2">
                <Button type="button" variant="secondary" size="sm" onClick={onClose}>Annuler</Button>
                <Button type="submit" variant={ACCENT_BTN[accent] ?? 'tech'} size="sm" loading={submitting}>
                  {mode === 'create' ? 'Créer' : 'Enregistrer'}
                </Button>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
